import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { createAppTheme } from '../../styles/theme'
import { useHubLembrado } from '../hubs/hubs'
import {
  ColorModeContext,
  DEFAULT_MODE,
  THEME_STORAGE_KEY,
  type ColorMode,
  type ColorModeContextValue,
  type RevealOrigin,
} from './ColorModeContext'

const REVEAL_DURATION = 480
const REVEAL_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'

function readStoredMode(): ColorMode {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    if (v === 'light' || v === 'dark') return v
  } catch {
    /* ignore */
  }
  return DEFAULT_MODE
}

/** Reflete o tema no <html> (data-theme dirige os tokens CSS; color-scheme, os nativos). */
function applyDomMode(mode: ColorMode) {
  const root = document.documentElement
  root.setAttribute('data-theme', mode)
  root.style.colorScheme = mode
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ColorMode>(readStoredMode)

  // Espelho síncrono do modo p/ o setMode estável ler sem fechar sobre estado velho.
  const modeRef = useRef(mode)
  modeRef.current = mode

  // Aplica o modo salvo no mount (garante consistência mesmo sem o script anti-flash).
  useEffect(() => {
    applyDomMode(modeRef.current)
  }, [])

  /** Comita a troca: o data-theme muda DENTRO do flushSync para o snapshot "new" já
   *  nascer com o tema novo (senão a View Transition captura o tema antigo). */
  const commit = useCallback((next: ColorMode) => {
    flushSync(() => setModeState(next))
    applyDomMode(next)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
  }, [])

  const setMode = useCallback(
    (next: ColorMode, origin?: RevealOrigin) => {
      if (next === modeRef.current) return

      const startViewTransition = document.startViewTransition?.bind(document)
      if (!startViewTransition || prefersReducedMotion()) {
        commit(next)
        return
      }

      const transition = startViewTransition(() => commit(next))
      const x = origin?.x ?? window.innerWidth / 2
      const y = origin?.y ?? 0
      const endRadius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))

      transition.ready
        .then(() => {
          document.documentElement.animate(
            {
              clipPath: [
                `circle(0px at ${x}px ${y}px)`,
                `circle(${endRadius}px at ${x}px ${y}px)`,
              ],
            },
            {
              duration: REVEAL_DURATION,
              easing: REVEAL_EASING,
              pseudoElement: '::view-transition-new(root)',
            },
          )
        })
        .catch(() => {
          /* transição cancelada — o commit já aplicou o tema */
        })
    },
    [commit],
  )

  const toggle = useCallback(
    (origin?: RevealOrigin) => setMode(modeRef.current === 'dark' ? 'light' : 'dark', origin),
    [setMode],
  )

  // O tema veste o hub guardado, que a rota mantém em dia (`useSincronizarHub`):
  // dentro de `/farm`, o verde da Agri8 e os neutros cinza; fora, o HubTransGrãos.
  const hub = useHubLembrado()
  const theme = useMemo(() => createAppTheme(mode, hub), [mode, hub])
  const value = useMemo<ColorModeContextValue>(() => ({ mode, setMode, toggle }), [mode, setMode, toggle])

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}
